import React from 'react';
import { StyleSheet, View } from 'react-native';
import Text from '../ui/Text';
import ServiceCard from './ServiceCard';
import CategoryRail from './CategoryRail';
import SectionHeader from './SectionHeader';
import type { UiComponent, UiTree } from '../../services/uiConfig';
import type { Category, CategoryKey, Service } from '../../data/services';
import { colors, screenPadding, spacing } from '../../theme';

/**
 * A published tree, drawn with the components the home screen already has.
 *
 * ---------------------------------------------------------------------------
 * IT RENDERS BLOCKS, NOT A SCREEN
 * ---------------------------------------------------------------------------
 * The address bar, the search field, the notification bell and the wallet are
 * chrome: they are how a customer gets anywhere, and no publication may remove
 * them. Only the CONTENT between them is configurable - banners, category
 * rails, service rails - so `HomeScreen` keeps drawing its own chrome and
 * hands this the middle.
 *
 * ---------------------------------------------------------------------------
 * AN UNKNOWN BLOCK IS SKIPPED, NOT AN ERROR
 * ---------------------------------------------------------------------------
 * `minAppVersion` on a publication is meant to stop an old build being handed
 * a component it cannot draw, and it is set by a person, so it will one day be
 * wrong. Skipping the block leaves the rest of the screen working; throwing
 * would take the whole page down over one tile.
 *
 * The same applies inside a block. A `service_carousel` naming an id this
 * device has not loaded renders the services it does have, and renders nothing
 * at all rather than an empty titled section if none of them resolve.
 */

type Props = {
  tree: UiTree;
  /** Everything this device knows about, to resolve ids and slugs against. */
  services: Service[];
  categories: Category[];
  serviceCardWidth: number;
  onPressService: (service: Service) => void;
  onPressCategory: (key: CategoryKey) => void;
  /** Drawn for a `banner_carousel`. The hero owns its own layout. */
  renderBanners: (
    items: Array<{ imageUrl: string; accessibilityLabel?: string }>,
  ) => React.ReactNode;
};

function ConfigRenderer({
  tree,
  services,
  categories,
  serviceCardWidth,
  onPressService,
  onPressCategory,
  renderBanners,
}: Props) {
  const categoryFor = (key: CategoryKey) =>
    categories.find(category => category.key === key);

  const block = (component: UiComponent): React.ReactNode => {
    switch (component.type) {
      case 'banner_carousel':
        return component.items.length ? renderBanners(component.items) : null;

      case 'category_grid': {
        /*
         * Resolved against what this device actually has. The server's
         * validator guarantees every slug EXISTED when the tree was published;
         * it cannot guarantee this handset has since loaded the catalogue, or
         * that the category is still active in this city.
         */
        const picked = component.categorySlugs
          .map(slug => categories.find(category => category.key === slug))
          .filter((category): category is Category => !!category);

        if (!picked.length) return null;

        return (
          <View style={styles.section}>
            {component.title ? <SectionHeader title={component.title} /> : null}
            {/*
              The swiped rail, matching the compiled Home above it.

              The block is called `category_grid` and this is not a wrapping
              grid - a real disagreement, and it was briefly fixed the other
              way, by wrapping both. The client asked for the rail back, and
              the thing that matters more than the block's name is that a
              published category block and the compiled one look the same:
              whichever a deployment happens to have, the page is the page.
            */}
            <CategoryRail categories={picked} onSelect={onPressCategory} />
          </View>
        );
      }

      case 'service_carousel': {
        /*
         * Matched on `key`, which IS the server's service id for anything that
         * came from the catalogue - `catalogue.ts` maps `key: row.id`. The
         * tree stores UUIDs and the validator refuses anything that is not
         * one, so the two line up without a lookup table.
         *
         * A service whose category this device does not know is dropped
         * rather than drawn: `ServiceCard` needs the category for its artwork,
         * and a card with no icon is worse than a shorter rail.
         */
        const picked = component.serviceIds
          .map(id => services.find(service => service.key === id))
          .filter(
            (service): service is Service =>
              !!service && !!categoryFor(service.category),
          );

        if (!picked.length) return null;

        return (
          <View style={styles.section}>
            {component.title ? <SectionHeader title={component.title} /> : null}
            {/*
              A grid, though the block is called a carousel.
              -------------------------------------------------------------
              The name is the server's column and changing it is a migration
              across three repositories; what it MEANS is "these services, on
              the home screen", and the layout was never the ops decision.
              Home draws its own list two across, and a published one that
              scrolled sideways instead would make the same page look like two
              different designs depending on whether anything was published.

              So the split is: WHICH services is ops', HOW they are shown is
              this app's. That is the same line `category_grid` above now
              follows.
            */}
            <View style={styles.serviceGrid}>
              {picked.map(service => (
                <ServiceCard
                  key={service.key}
                  service={service}
                  // Non-null: the filter above dropped anything unresolvable.
                  category={categoryFor(service.category)!}
                  width={serviceCardWidth}
                  onPress={onPressService}
                />
              ))}
            </View>
          </View>
        );
      }

      case 'text_block':
        return (
          <View style={styles.section}>
            <Text
              variant={component.style === 'heading' ? 'titleLg' : 'body'}
              color={
                component.style === 'caption' ? colors.inkMuted : colors.ink
              }
            >
              {component.text}
            </Text>
          </View>
        );

      case 'spacer':
        return <View style={styles[spacerFor(component.size)]} />;

      default:
        // A block this build does not know. See the note above.
        return null;
    }
  };

  return (
    <>
      {tree.components.map(component => (
        <React.Fragment key={component.id}>{block(component)}</React.Fragment>
      ))}
    </>
  );
}

const spacerFor = (size?: string): 'spacerSm' | 'spacerMd' | 'spacerLg' => {
  if (size === 'sm') return 'spacerSm';
  if (size === 'lg') return 'spacerLg';
  return 'spacerMd';
};

const styles = StyleSheet.create({
  /* The grid measures itself off this box, so the page gutter lives here
     rather than inside the component. */
  /* Two per row, wrapping. `flex-start` so a final odd card sits at the left
     where the column is rather than stretching across the row. */
  serviceGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingHorizontal: screenPadding,
    gap: spacing.sm,
  },
  section: {
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  rail: {
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  spacerSm: { height: spacing.sm },
  spacerMd: { height: spacing.lg },
  spacerLg: { height: spacing.xl },
});

export default ConfigRenderer;
